import React, { useState, useCallback } from 'react';
import { Card, Button, Alert } from 'react-bootstrap';
import { useParams, useHistory } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faFileAlt,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faArrowLeft,
  faFileInvoice,
} from '@fortawesome/free-solid-svg-icons';
import PageTitle from '../../layouts/PageTitle';

const SkatteverketImporter = () => {
  const { companyId } = useParams();
  const history = useHistory();

  const [file, setFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState(null);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setFileError(null);
    setImportResult(null);

    if (rejectedFiles && rejectedFiles.length > 0) {
      setFileError('Endast CSV-filer är tillåtna. Välj en fil som slutar med .csv');
      setFile(null);
      return;
    }

    if (acceptedFiles && acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];

      // Additional validation: check file extension
      if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
        setFileError('Filen måste vara en CSV-fil (.csv)');
        setFile(null);
        return;
      }

      setFile(selectedFile);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    multiple: false,
    noClick: false,
    noKeyboard: false,
  });

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!file) {
      alert('Välj en CSV-fil för att importera.');
      return;
    }

    setLoading(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const apiUrl = getApiUrl(
        `${companyId}/accounting/reconciliation/skatteverket/import`
      );
      const response = await axios.post(apiUrl, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setImportResult({
        success: true,
        ...response.data
      });
    } catch (error) {
      console.error('Error importing Skatteverket statement:', error);
      setImportResult({
        success: false,
        error: error.response?.data?.error || error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Importera" motherMenu="Skattekonto" />

      <div className="row">
        <div className="col-12">
          <Button
            variant="link"
            className="mb-3 text-decoration-none"
            onClick={() => history.push(`/${companyId}/skattekonto`)}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Tillbaka till Skattekonto
          </Button>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-8 mx-auto">
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white py-3">
              <h4 className="mb-0">
                <FontAwesomeIcon icon={faUpload} className="me-2" />
                Importera Skattekonto CSV
              </h4>
            </Card.Header>
            <Card.Body className="p-4">
              {/* Information Box */}
              <Alert variant="info" className="mb-4">
                <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                <strong>Så här fungerar det:</strong>
                <ul className="mb-0 mt-2">
                  <li>Ladda ner CSV-fil från Skatteverket (Mina sidor → Skattekonto → Kontoutdrag eller Bokförda transaktioner)</li>
                  <li>Accepterade format: Kontoutdrag (3 kolumner) och Bokförda transaktioner (4 kolumner)</li>
                  <li>Filen importeras automatiskt och transaktioner klassificeras (Moms, Inkomstskatt, Arbetsgivaravgift, etc.)</li>
                </ul>
              </Alert>

              {/* File Error Alert */}
              {fileError && (
                <Alert variant="warning" className="mb-3">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  {fileError}
                </Alert>
              )}

              {/* File Drop Zone */}
              <div
                {...getRootProps()}
                className={`border border-2 border-dashed rounded p-5 text-center mb-4 ${
                  isDragActive ? 'border-primary bg-light' : fileError ? 'border-danger' : 'border-secondary'
                }`}
                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
              >
                <input {...getInputProps()} accept=".csv" />
                <FontAwesomeIcon
                  icon={faFileAlt}
                  className="display-3 mb-3"
                  style={{ color: isDragActive ? '#007bff' : fileError ? '#dc3545' : '#6c757d' }}
                />
                {isDragActive ? (
                  <p className="h5 mb-0 text-primary">
                    Släpp CSV-filen här...
                  </p>
                ) : (
                  <div>
                    <p className="h5 mb-2">
                      Dra och släpp CSV-fil här, eller klicka för att välja
                    </p>
                    <p className="text-muted mb-0">
                      Accepterade format: .csv
                    </p>
                    {file && (
                      <div className="mt-3">
                        <p className="mb-0 text-success">
                          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                          <strong>Vald fil:</strong> {file.name}
                        </p>
                        <p className="text-muted small mb-0">
                          Storlek: {(file.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleUpload}
                  disabled={!file || loading}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                      Importerar...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faUpload} className="me-2" />
                      Importera Skattekonto CSV
                    </>
                  )}
                </Button>
              </div>

              {/* Import Result */}
              {importResult && (
                <div className="mt-4">
                  {importResult.success ? (
                    <Alert variant="success">
                      <div className="d-flex">
                        <FontAwesomeIcon icon={faCheckCircle} className="mt-1 me-3" size="lg" />
                        <div className="flex-grow-1">
                          <h5 className="alert-heading">Import lyckades!</h5>
                          {importResult.statistics && (
                            <div>
                              <p className="mb-1">
                                <strong>Totalt:</strong> {importResult.statistics.total} transaktioner
                              </p>
                              <p className="mb-1">
                                <strong>Importerade:</strong> {importResult.statistics.successful} st
                              </p>
                              {importResult.statistics.duplicates > 0 && (
                                <p className="mb-1 text-warning">
                                  <strong>Dubbletter (hoppades över):</strong> {importResult.statistics.duplicates} st
                                </p>
                              )}
                              {importResult.statistics.failed > 0 && (
                                <p className="mb-1 text-danger">
                                  <strong>Misslyckades:</strong> {importResult.statistics.failed} st
                                </p>
                              )}
                              {importResult.periodStartDate && importResult.periodEndDate && (
                                <p className="mb-0 text-muted small">
                                  Period: {importResult.periodStartDate} till {importResult.periodEndDate}
                                </p>
                              )}
                            </div>
                          )}
                          {importResult.errors && importResult.errors.length > 0 && (
                            <div className="mt-3">
                              <p className="fw-bold mb-2">Problem med vissa rader:</p>
                              <ul className="mb-0 small">
                                {importResult.errors.slice(0, 5).map((err, index) => (
                                  <li key={index}>
                                    Rad {err.row}: {err.error || err.message}
                                  </li>
                                ))}
                                {importResult.errors.length > 5 && (
                                  <li className="text-muted">
                                    ... och {importResult.errors.length - 5} till
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                          <div className="mt-3">
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => history.push(`/${companyId}/skattekonto`)}
                            >
                              Visa importerade transaktioner
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Alert>
                  ) : (
                    <Alert variant="danger">
                      <div className="d-flex">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="mt-1 me-3" size="lg" />
                        <div>
                          <h5 className="alert-heading">Import misslyckades</h5>
                          <p className="mb-0">
                            <strong>Fel:</strong> {importResult.error || 'Ett okänt fel uppstod'}
                          </p>
                          <p className="mb-0 mt-2 small text-muted">
                            Kontrollera att CSV-filen är i rätt format och försök igen.
                          </p>
                        </div>
                      </div>
                    </Alert>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );
};

export default SkatteverketImporter;
